import React, { useState, useEffect, useRef } from 'react';
import ScenariosTab from './ScenariosTab';
import scenarioService from '../services/scenario-service';
import GuidanceModal from './GuidanceModal';
import GoogleAdInterstitial from './GoogleAdInterstitial';
import DisclaimerModal from './DisclaimerModal';
import StructuredData from './StructuredData';
import './CareerComparisonCalculator.css';
import { Link, useNavigate } from 'react-router-dom';
import { CareerComparisonData, CareerPathData, SalaryGrowthData, CareerComparisonSummary } from '../types/careerComparison';
import {
  calculateSalaryProgression,
  buildCashFlows,
  buildCumulativeNetIncome,
  findBreakEven,
  compareCareers,
  calculateSensitivity,
  calculateTotalEarnings as engineTotalEarnings,
  calculateOpportunityCost as engineOpportunityCost
} from './career/careerEngine';
import { BaseScenarioData } from '../types/shared';
import { safeParseFloat } from '../utils/inputValidation';
import { formatCurrencyWithColor as formatCurrencyWithColorShared } from '../utils/inputFormatters';
import ErrorBoundary from './ErrorBoundary';
import { TylerState, updateReact, useTylerStateUpdate } from '../utils/TylerState';
import { debugLog } from '../utils/debugLogger';
import careerPDFService from '../services/career-pdf-service';
import careerScenariosService from '../services/career-scenarios';
import { useCountry } from '../contexts/CountryContext';
import { CareerTemplate, getCareerTemplatesForCountry } from './career/careerTemplates';
import { US_STATE_TAX_RATES } from '../utils/usStateTaxRates';
// Modular components created - will integrate after JSX structure cleanup
// import BasicInfoInputs from './careerInputs/BasicInfoInputs';
// import EducationInputs from './careerInputs/EducationInputs';
// import SalaryGrowthInputs from './careerInputs/SalaryGrowthInputs';

interface Consideration {
  type: 'risk' | 'opportunity';
  priority: number; // Higher = more important (0-100)
  title: string;
  description: string;
  icon: string; // Emoji
}


const NO_GROWTH: SalaryGrowthData = { type: 'no-change', percentageGrowth: Array(10).fill(0), dollarGrowth: Array(10).fill(0) };

/** First-visit defaults. All dollars are today's dollars; raises are above inflation. */
const DEFAULT_PATH_A: CareerPathData = {
  currentSalary: 50000,
  nonSalaryBenefits: 5000,
  currentAge: 25,
  retirementAge: 65,
  province: 'Ontario',
  salaryGrowth: NO_GROWTH,
  benefitsGrowthRate: 0,
  hasEducation: false,
  yearsInSchool: 4,
  annualSchoolCost: 25000,
  expectedStartingSalary: 65000,
  incomeDuringSchool: 0,
  monthsAfterSchoolToStartJob: 0,
  scenarioName: 'Career Path A'
};

const DEFAULT_PATH_B: CareerPathData = {
  ...DEFAULT_PATH_A,
  currentSalary: 0,
  nonSalaryBenefits: 0,
  salaryGrowth: { type: 'percentage', percentageGrowth: [5, 4, 3, 3, 2, 2, 2, 2, 2, 2], dollarGrowth: Array(10).fill(0) },
  hasEducation: true,
  scenarioName: 'Career Path B'
};

const CareerComparisonCalculator: React.FC<{
  user: any;
  guestScenario?: any;
  onSaveScenario?: (data: any) => void
}> = ({ user, guestScenario, onSaveScenario }) => {

  // Subscribe to TylerState updates
  useTylerStateUpdate();

  // Get country from global context
  const { isUSA, countryName } = useCountry();

  // Set page title and meta description for SEO - Enhanced with keywords
  useEffect(() => {
    if (isUSA) {
      document.title = `Career Change Calculator USA 2026 | Compare Salaries & Education ROI | Larry's Help`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `Free career comparison calculator for USA. Compare two career paths with after-tax calculations, education costs, salary growth, and break-even analysis. MBA ROI, trade school vs college, career change at 30/40/50. Save scenarios.`);
      }
    } else {
      document.title = `Career Change Calculator Canada 2026 | Compare Salaries with CPP/EI Tax | Larry's Help`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', `Free career comparison calculator for Canada. Compare two career paths with provincial taxes, CPP/EI deductions, OSAP costs, and RRSP impact. MBA ROI, skilled trades, career change at any age. Save unlimited scenarios.`);
      }
    }
  }, [isUSA]);

  // Guidance modal state
  const [guidanceModal, setGuidanceModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
  }>({
    isOpen: false,
    title: '',
    content: ''
  });

  // Modal states
  const [showAdModal, setShowAdModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Calculation state
  const [hasCalculated, setHasCalculated] = useState(false);

  // Section navigation
  const [activeSection, setActiveSection] = useState<string>('inputs');

  // Start Simple mode state
  const [startSimpleMode, setStartSimpleMode] = useState(true);

  // Which ready-made comparison is loaded, so its card can show as selected
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);

  // Investment opportunity cost state (optional feature, Advanced Mode only)
  const [showOpportunityCost, setShowOpportunityCost] = useState(false);
  const [investmentReturnRate, setInvestmentReturnRate] = useState(5); // Default 5% like housing calculator

  // Auto-save timeout ref (same pattern as car calculator)
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to format numbers in thousands or millions
  const formatInThousands = (value: number): string => {
    const absValue = Math.abs(value);
    if (absValue >= 1000000) {
      const inMillions = value / 1000000;
      return `${inMillions.toFixed(1)}M`;
    } else if (absValue >= 1000) {
      const inThousands = Math.round(value / 1000);
      return `${inThousands}k`;
    } else {
      return `${Math.round(value)}`;
    }
  };

  // Currency formatter matching housing calculator pattern
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Color-coded currency (RBC standard: green income, red expense)
  const formatCurrencyWithColor = (value: number, colorType: 'positive' | 'negative' | 'neutral' = 'neutral') => {
    return formatCurrencyWithColorShared(value, colorType, formatCurrency);
  };

  // Safe input change handler with validation
  const handleSafeInputChange = (career: CareerPathData, setCareer: (career: CareerPathData) => void, field: keyof CareerPathData, value: string) => {
    const validation = safeParseFloat(value, {
      min: field.includes('Age') ? 16 : 0,
      max: field.includes('Age') ? 100 : (field.includes('Salary') || field.includes('Benefits') || field.includes('Cost') ? 10000000 : 50)
    });

    if (validation.isValid) {
      setCareer({
        ...career,
        [field]: validation.value
      });
    }
  };

  // Helper function to show guidance (compatible with InteractiveSlider)
  const showGuidance = (title: string, content: string) => {
    setGuidanceModal({
      isOpen: true,
      title,
      content
    });
  };

  // PDF Export Handler
  const handleExportPDF = () => {
    if (!hasCalculated) {
      showGuidance('No Results', 'Please calculate your career comparison first before exporting to PDF.');
      return;
    }

    const pathAData = calculateTotalEarnings(careerPathA);
    const pathBData = calculateTotalEarnings(careerPathB);

    const pdfResults = {
      currentCareer: {
        totalEarnings: pathAData.beforeTax,
        totalTaxes: pathAData.taxes,
        netEarnings: pathAData.afterTax,
        finalBalance: pathAData.afterTax
      },
      newCareer: {
        totalEarnings: pathBData.beforeTax,
        totalTaxes: pathBData.taxes,
        netEarnings: pathBData.afterTax,
        finalBalance: pathBData.afterTax
      },
      comparison: {
        earningsDifference: pathBData.beforeTax - pathAData.beforeTax,
        taxesDifference: (pathBData.beforeTax - pathBData.afterTax) - (pathAData.beforeTax - pathAData.afterTax),
        netEarningsDifference: pathBData.afterTax - pathAData.afterTax,
        finalBalanceDifference: pathBData.afterTax - pathAData.afterTax,
        breakEvenAge: results.breakevenAge
      }
    };

    careerPDFService.generateCareerComparisonPDF(
      careerPathA,
      careerPathB,
      pdfResults
    );

    showGuidance('PDF Generated', 'Your career comparison report has been downloaded successfully!');
  };

  // Engine wrappers (math lives in ./career/careerEngine.ts)
  const calculateDetailedCashFlows = (career: CareerPathData) => buildCashFlows(career, isUSA);
  const calculateCumulativeNetIncome = (career: CareerPathData) => buildCumulativeNetIncome(career, isUSA);
  const findBreakEvenPoint = () => findBreakEven(careerPathA, careerPathB, isUSA);
  
  // Seed TylerState on first visit
  useEffect(() => {
    if (!TylerState.career.careerA.inputs?.salaryGrowth) {
      TylerState.career.careerA.inputs = { ...DEFAULT_PATH_A };
    }
    if (!TylerState.career.careerB.inputs?.salaryGrowth) {
      TylerState.career.careerB.inputs = { ...DEFAULT_PATH_B };
    }
    updateReact();
  }, []);

  // Sync with global country context - update province/state when country changes
  useEffect(() => {
    const defaultLocation = isUSA ? 'CA' : 'Ontario'; // CA = California, Ontario for Canada

    // Update both career paths when country changes
    if (TylerState.career.careerA.inputs) {
      const currentProvince = TylerState.career.careerA.inputs.province;
      // Only update if current value doesn't match the country
      const isCurrentlyUS = currentProvince?.length === 2 && US_STATE_TAX_RATES[currentProvince];
      if (isUSA !== isCurrentlyUS) {
        TylerState.career.careerA.inputs = { ...TylerState.career.careerA.inputs, province: defaultLocation };
      }
    }
    if (TylerState.career.careerB.inputs) {
      const currentProvince = TylerState.career.careerB.inputs.province;
      const isCurrentlyUS = currentProvince?.length === 2 && US_STATE_TAX_RATES[currentProvince];
      if (isUSA !== isCurrentlyUS) {
        TylerState.career.careerB.inputs = { ...TylerState.career.careerB.inputs, province: defaultLocation };
      }
    }
    updateReact();
  }, [isUSA]);

  // Helper to get career data with defaults
  const getCareerData = (inputs: any, scenarioName: string): CareerPathData =>
    inputs?.salaryGrowth ? (inputs as CareerPathData) : { ...DEFAULT_PATH_A, scenarioName };

  // Read career paths from TylerState with safe defaults
  const careerPathA = getCareerData(TylerState.career.careerA.inputs, 'Career Path A');
  const careerPathB = getCareerData(TylerState.career.careerB.inputs, 'Career Path B');

  // Setters that update TylerState (removed autosave-on-typing per Tyler)
  const setCareerPathA = (newData: CareerPathData) => {
    TylerState.career.careerA.inputs = newData;
    updateReact();
  };

  const setCareerPathB = (newData: CareerPathData) => {
    TylerState.career.careerB.inputs = newData;
    updateReact();
  };

  const calculateTotalEarnings = (career: CareerPathData) => engineTotalEarnings(career, isUSA);

  const calculateComparison = () => compareCareers(careerPathA, careerPathB, isUSA);

  // Separate calculation function that can be called after ad
  const performCalculation = async () => {
    // Calculate results with detailed data
    const pathAData = calculateTotalEarnings(careerPathA);
    const pathBData = calculateTotalEarnings(careerPathB);
    const comparisonResults = calculateComparison();

    // Calculate detailed cash flows for year-by-year analysis
    const pathACashFlows = calculateDetailedCashFlows(careerPathA);
    const pathBCashFlows = calculateDetailedCashFlows(careerPathB);

    // Store in TylerState with enhanced metrics (matching car/housing pattern)
    TylerState.career.careerA.outputs = {
      lifetimeEarnings: pathAData.beforeTax,
      netValue: pathAData.afterTax,
      totalTaxes: pathAData.taxes,
      totalBenefits: pathAData.benefits,
      netEducationCost: pathAData.netEducationCost,
      workingYears: pathAData.workingYears,
      averageAnnualBeforeTax: pathAData.averageAnnualBeforeTax,
      averageAnnualAfterTax: pathAData.averageAnnualAfterTax,
      cashFlows: pathACashFlows
    };
    TylerState.career.careerB.outputs = {
      lifetimeEarnings: pathBData.beforeTax,
      netValue: pathBData.afterTax,
      totalTaxes: pathBData.taxes,
      totalBenefits: pathBData.benefits,
      netEducationCost: pathBData.netEducationCost,
      workingYears: pathBData.workingYears,
      averageAnnualBeforeTax: pathBData.averageAnnualBeforeTax,
      averageAnnualAfterTax: pathBData.averageAnnualAfterTax,
      cashFlows: pathBCashFlows
    };
    TylerState.career.comparison = {
      winner: comparisonResults.winner,
      difference: Math.abs(comparisonResults.difference),
      results: comparisonResults,
      breakevenYear: comparisonResults.breakevenYear,
      breakevenAge: comparisonResults.breakevenAge
    };

    updateReact();

    debugLog('CAREER CALCULATOR OUTPUTS', {
      careerA: TylerState.career.careerA,
      careerB: TylerState.career.careerB,
      comparison: TylerState.career.comparison
    });

    // Auto-save after calculation (if user has scenario names)
    const currentUser = TylerState.user;
    if (currentUser) {
      if (TylerState.careerNameA && TylerState.careerNameA.trim()) {
        await autoSaveScenario('A');
      }
      if (TylerState.careerNameB && TylerState.careerNameB.trim()) {
        await autoSaveScenario('B');
      }
    }

    setHasCalculated(true);
    setActiveSection('summary'); // Navigate to first output section
  };

  // Calculate top risks and opportunities based on comparison
  // Risks and opportunities are written about the path that includes education (either side).
  const calculateConsiderations = (comparison: CareerComparisonSummary): { risks: Consideration[]; opportunities: Consideration[] } => {
    const considerations: Consideration[] = [];

    const educationOnA = careerPathA.hasEducation && !careerPathB.hasEducation;
    const educationPath = educationOnA ? careerPathA : careerPathB;
    const basePath = educationOnA ? careerPathB : careerPathA;
    const educationTotals = educationOnA ? comparison.pathA : comparison.pathB;
    const baseTotals = educationOnA ? comparison.pathB : comparison.pathA;
    const eduName = educationOnA ? comparison.pathAName : comparison.pathBName;
    const baseName = educationOnA ? comparison.pathBName : comparison.pathAName;
    const difference = educationTotals.afterTax - baseTotals.afterTax;
    const breakevenYear = educationOnA
      ? (() => { const swapped = findBreakEven(careerPathB, careerPathA, isUSA); return swapped && !swapped.aheadFromStart ? swapped.year : 0; })()
      : comparison.breakevenYear;

    // RISKS (🔴)

    // 1. Late Break-Even Point
    if (breakevenYear > 15) {
      considerations.push({
        type: 'risk',
        priority: 100,
        title: 'Very Late Break-Even Point',
        description: `Takes ${breakevenYear} years to break even. If plans change before then, you may never recover the investment.`,
        icon: '⏰'
      });
    } else if (breakevenYear > 10) {
      considerations.push({
        type: 'risk',
        priority: 75,
        title: 'Late Break-Even Point',
        description: `Takes ${breakevenYear} years to break even. Consider if you'll stay in this career that long.`,
        icon: '⏰'
      });
    }

    // 2. Negative Lifetime Value
    if (difference < 0) {
      const absLoss = Math.abs(difference);
      considerations.push({
        type: 'risk',
        priority: 95,
        title: `${eduName} Earns Less Over Lifetime`,
        description: `${eduName} earns $${absLoss.toLocaleString()} less than ${baseName} over your career. Financial return is negative.`,
        icon: '📉'
      });
    }

    // 3. High Education Costs
    if (educationPath.hasEducation && educationPath.annualSchoolCost > 25000) {
      const totalCost = educationPath.annualSchoolCost * educationPath.yearsInSchool;
      considerations.push({
        type: 'risk',
        priority: 70,
        title: 'High Education Costs',
        description: `$${totalCost.toLocaleString()} total tuition. Consider scholarships, part-time study, or employer sponsorship.`,
        icon: '💸'
      });
    }

    // 4. Lost Income During School
    if (educationPath.hasEducation && educationPath.yearsInSchool >= 2 && educationPath.incomeDuringSchool === 0) {
      const lostIncome = basePath.currentSalary * educationPath.yearsInSchool;
      considerations.push({
        type: 'risk',
        priority: 65,
        title: 'Lost Income During School',
        description: `$${lostIncome.toLocaleString()} in foregone salary. Can you afford to not work for ${educationPath.yearsInSchool} years?`,
        icon: '💰'
      });
    }

    // 5. Optimistic Salary Growth
    const avgSalaryGrowth = educationPath.salaryGrowth.type === 'percentage'
      ? educationPath.salaryGrowth.percentageGrowth[0]
      : 0;
    if (avgSalaryGrowth > 3) {
      considerations.push({
        type: 'risk',
        priority: 60,
        title: 'Optimistic Salary Growth Assumption',
        description: `Assumes ${avgSalaryGrowth}% raises above inflation. Long-run real wage growth is closer to 1-2%. Run sensitivity analysis to test this.`,
        icon: '📊'
      });
    }

    // 6. Long Job Search After Graduation
    if (educationPath.hasEducation && educationPath.monthsAfterSchoolToStartJob > 6) {
      considerations.push({
        type: 'risk',
        priority: 55,
        title: 'Extended Job Search Period',
        description: `${educationPath.monthsAfterSchoolToStartJob} months to find a job. Budget for living expenses during this gap.`,
        icon: '🔍'
      });
    }

    // 7. Age Risk - Late Career Change
    if (educationPath.hasEducation && educationPath.currentAge > 35) {
      considerations.push({
        type: 'risk',
        priority: 50,
        title: 'Late Career Change',
        description: `Starting school at age ${educationPath.currentAge}. Fewer working years to recover investment. Break-even analysis critical.`,
        icon: '🕐'
      });
    }

    // 8. Low Starting Salary After School
    if (educationPath.hasEducation && educationPath.expectedStartingSalary < basePath.currentSalary) {
      const difference = basePath.currentSalary - educationPath.expectedStartingSalary;
      considerations.push({
        type: 'risk',
        priority: 45,
        title: 'Lower Starting Salary',
        description: `Post-education salary ($${educationPath.expectedStartingSalary.toLocaleString()}) is $${difference.toLocaleString()} less than current salary.`,
        icon: '📉'
      });
    }

    // 9. Marginal Gain Despite High Cost
    if (educationPath.hasEducation && difference > 0 && difference < 100000) {
      const totalCost = educationPath.annualSchoolCost * educationPath.yearsInSchool;
      if (totalCost > difference / 2) {
        considerations.push({
          type: 'risk',
          priority: 40,
          title: 'Marginal Financial Gain',
          description: `Lifetime gain of $${difference.toLocaleString()} barely justifies the cost and effort. Consider non-financial benefits.`,
          icon: '⚖️'
        });
      }
    }

    // 10. Career Uncertainty / No Guarantee
    if (educationPath.hasEducation) {
      considerations.push({
        type: 'risk',
        priority: 35,
        title: 'Career Path Not Guaranteed',
        description: `No guarantee you'll complete school, get hired, or stay in the field. Have a backup plan.`,
        icon: '❓'
      });
    }

    // OPPORTUNITIES (🟢)

    // 1. Strong Lifetime Earnings Increase
    if (difference > 500000) {
      considerations.push({
        type: 'opportunity',
        priority: 100,
        title: 'Significant Lifetime Earnings Boost',
        description: `${eduName} earns $${difference.toLocaleString()} more over your career. This is a game-changing financial opportunity.`,
        icon: '🚀'
      });
    } else if (difference > 200000) {
      considerations.push({
        type: 'opportunity',
        priority: 85,
        title: 'Strong Lifetime Earnings Increase',
        description: `${eduName} earns $${difference.toLocaleString()} more over your career. Solid financial return.`,
        icon: '💪'
      });
    } else if (difference > 50000) {
      considerations.push({
        type: 'opportunity',
        priority: 65,
        title: 'Moderate Lifetime Earnings Increase',
        description: `${eduName} earns $${difference.toLocaleString()} more over your career. A meaningful financial advantage.`,
        icon: '📊'
      });
    }

    // 2. Quick Break-Even
    if (breakevenYear > 0 && breakevenYear <= 5) {
      considerations.push({
        type: 'opportunity',
        priority: 90,
        title: 'Fast Break-Even Point',
        description: `You recover your investment in just ${breakevenYear} years. Low financial risk.`,
        icon: '⚡'
      });
    } else if (breakevenYear > 0 && breakevenYear <= 8) {
      considerations.push({
        type: 'opportunity',
        priority: 70,
        title: 'Reasonable Break-Even Point',
        description: `Break-even in ${breakevenYear} years is manageable for most career changers.`,
        icon: '✅'
      });
    }

    // 3. High Earning Potential
    if (educationPath.expectedStartingSalary > 100000) {
      considerations.push({
        type: 'opportunity',
        priority: 80,
        title: 'High Earning Potential',
        description: `Starting salary of $${educationPath.expectedStartingSalary.toLocaleString()} puts you in top income bracket. Strong career prospects.`,
        icon: '💎'
      });
    }

    // 4. Salary Growth Advantage
    if (educationTotals.averageAnnualAfterTax > baseTotals.averageAnnualAfterTax * 1.2) {
      const percentIncrease = Math.round(((educationTotals.averageAnnualAfterTax - baseTotals.averageAnnualAfterTax) / baseTotals.averageAnnualAfterTax) * 100);
      considerations.push({
        type: 'opportunity',
        priority: 75,
        title: 'Higher Average Annual Earnings',
        description: `${eduName} averages ${percentIncrease}% more per year. Compounds over time for significant wealth building.`,
        icon: '📈'
      });
    }

    // 5. Long Career Runway
    if (educationPath.currentAge < 30 && educationPath.retirementAge - educationPath.currentAge > 35) {
      considerations.push({
        type: 'opportunity',
        priority: 70,
        title: 'Long Career Runway',
        description: `${educationPath.retirementAge - educationPath.currentAge} working years ahead. Plenty of time to maximize ROI on education.`,
        icon: '🛤️'
      });
    }

    // 6. Affordable Education
    if (educationPath.hasEducation && educationPath.annualSchoolCost < 15000) {
      considerations.push({
        type: 'opportunity',
        priority: 65,
        title: 'Affordable Education',
        description: `Tuition of $${educationPath.annualSchoolCost.toLocaleString()}/year is manageable. Low debt burden.`,
        icon: '🎓'
      });
    }

    // 7. Income During School
    if (educationPath.hasEducation && educationPath.incomeDuringSchool > 0) {
      const annualIncome = educationPath.incomeDuringSchool;
      considerations.push({
        type: 'opportunity',
        priority: 60,
        title: 'Earning While Learning',
        description: `$${annualIncome.toLocaleString()}/year during school reduces financial strain and debt.`,
        icon: '💵'
      });
    }

    // 8. Career Advancement Potential
    if (educationPath.hasEducation && educationPath.expectedStartingSalary > basePath.currentSalary * 1.3) {
      considerations.push({
        type: 'opportunity',
        priority: 55,
        title: 'Strong Career Advancement',
        description: `Education unlocks roles with ${Math.round((educationPath.expectedStartingSalary / basePath.currentSalary - 1) * 100)}% salary jump. Opens doors to management/specialized roles.`,
        icon: '🚪'
      });
    }

    // 9. Tax Benefits (Higher Earning = Higher Savings)
    if (educationTotals.beforeTax - educationTotals.afterTax > baseTotals.beforeTax - baseTotals.afterTax) {
      const additionalTax = (educationTotals.beforeTax - educationTotals.afterTax) - (baseTotals.beforeTax - baseTotals.afterTax);
      considerations.push({
        type: 'opportunity',
        priority: 50,
        title: 'You\'ll Earn Enough to Pay More Tax',
        description: `${eduName} pays $${additionalTax.toLocaleString()} more in lifetime taxes because you earn more. Higher income = more tax, but that\'s success!`,
        icon: '📊'
      });
    }

    // 10. Non-Financial Benefits
    if (educationPath.hasEducation) {
      considerations.push({
        type: 'opportunity',
        priority: 45,
        title: 'Non-Financial Benefits',
        description: `Education can provide job satisfaction, career flexibility, personal growth, and social status beyond just money.`,
        icon: '🌟'
      });
    }

    // Sort by priority and separate
    const risks = considerations
      .filter(c => c.type === 'risk')
      .sort((a, b) => b.priority - a.priority)
      .slice(0, 5);

    const opportunities = considerations
      .filter(c => c.type === 'opportunity')
      .sort((a, b) => b.priority - a.priority)
      .slice(0, 5);

    return { risks, opportunities };
  };

  // Engine wrappers (math lives in ./career/careerEngine.ts)
  const calculateSensitivityAnalysis = (baseComparison: { breakevenYear: number }) =>
    calculateSensitivity(careerPathA, careerPathB, baseComparison.breakevenYear, isUSA);

  const calculateOpportunityCost = (educationPath: CareerPathData) =>
    engineOpportunityCost(careerPathA, educationPath, investmentReturnRate);

  // Convert CareerComparisonData to CareerPathData
  const convertToCareerPathData = (data: CareerComparisonData): CareerPathData => {
    // Use defaults for all numeric fields to prevent NaN propagation
    // Check for new array fields first, fall back to legacy single rate
    const growthType = data.salaryGrowthType || 'percentage';
    const defaultRate = data.salaryGrowthRate ?? 2;

    // Restore full arrays if saved, otherwise fill with legacy single rate
    const percentageGrowth = data.salaryGrowthPercentages && data.salaryGrowthPercentages.length > 0
      ? [...data.salaryGrowthPercentages, ...Array(10 - data.salaryGrowthPercentages.length).fill(defaultRate)].slice(0, 10)
      : Array(10).fill(defaultRate);

    const dollarGrowth = data.salaryGrowthDollars && data.salaryGrowthDollars.length > 0
      ? [...data.salaryGrowthDollars, ...Array(10 - data.salaryGrowthDollars.length).fill(0)].slice(0, 10)
      : Array(10).fill(0);

    return {
      currentSalary: data.currentSalary ?? 50000,
      nonSalaryBenefits: data.nonSalaryBenefits ?? 0,
      currentAge: data.currentAge ?? 25,
      retirementAge: data.retirementAge ?? 65,
      province: data.province || 'Ontario',
      salaryGrowth: {
        type: growthType,
        percentageGrowth,
        dollarGrowth
      },
      benefitsGrowthRate: data.benefitsGrowthRate ?? 2,
      hasEducation: data.hasEducation ?? false,
      yearsInSchool: data.yearsInSchool ?? 0,
      annualSchoolCost: data.annualSchoolCost ?? 0,
      expectedStartingSalary: data.expectedStartingSalary ?? 50000,
      incomeDuringSchool: data.incomeDuringSchool ?? 0,
      monthsAfterSchoolToStartJob: data.monthsAfterSchoolToStartJob ?? 0,
      scenarioName: data.scenarioName || ''
    };
  };

  // Convert CareerPathData to CareerComparisonData
  const convertToCareerComparisonData = (data: CareerPathData): CareerComparisonData => {
    return {
      currentAge: data.currentAge || 25,
      currentSalary: data.currentSalary || 50000,
      nonSalaryBenefits: data.nonSalaryBenefits || 0,
      retirementAge: data.retirementAge || 65,
      // Legacy field for backward compatibility
      salaryGrowthRate: data.salaryGrowth?.type === 'percentage'
        ? (data.salaryGrowth.percentageGrowth?.[0] || 2)
        : 0,
      benefitsGrowthRate: data.benefitsGrowthRate || 2,
      province: data.province || 'Ontario',
      hasEducation: data.hasEducation || false,
      yearsInSchool: data.yearsInSchool || 0,
      annualSchoolCost: data.annualSchoolCost || 0,
      expectedStartingSalary: data.expectedStartingSalary || 50000,
      incomeDuringSchool: data.incomeDuringSchool || 0,
      monthsAfterSchoolToStartJob: data.monthsAfterSchoolToStartJob || 0,
      scenarioName: data.scenarioName || '',
      // New fields to preserve full salary growth arrays
      salaryGrowthType: data.salaryGrowth?.type || 'percentage',
      salaryGrowthPercentages: data.salaryGrowth?.percentageGrowth || Array(10).fill(2),
      salaryGrowthDollars: data.salaryGrowth?.dollarGrowth || Array(10).fill(0)
    };
  };

  const handleLoadCareerPathA = (data: CareerComparisonData, scenarioName?: string) => {
    const finalScenarioName = scenarioName || 'Career A';

    debugLog('📂 LOADING CAREER DATA (Career A)', { name: finalScenarioName });

    // Update TylerState (single source of truth)
    TylerState.careerNameA = finalScenarioName;
    updateReact();

    setCareerPathA(convertToCareerPathData(data));
  };

  const handleLoadCareerPathB = (data: CareerComparisonData, scenarioName?: string) => {
    const finalScenarioName = scenarioName || 'Career B';

    debugLog('📂 LOADING CAREER DATA (Career B)', { name: finalScenarioName });

    // Update TylerState (single source of truth)
    TylerState.careerNameB = finalScenarioName;
    updateReact();

    setCareerPathB(convertToCareerPathData(data));
  };

  // Wrapper functions for ScenariosTab component
  const handleLoadScenarioToA = (data: BaseScenarioData, scenarioName?: string) => {
    handleLoadCareerPathA(data as CareerComparisonData, scenarioName);
  };

  const handleLoadScenarioToB = (data: BaseScenarioData, scenarioName?: string) => {
    handleLoadCareerPathB(data as CareerComparisonData, scenarioName);
  };

  /** Loads both sides of a ready-made comparison and clears any stale results. */
  const handleLoadTemplate = (template: CareerTemplate) => {
    TylerState.career.careerA.inputs = { ...template.pathA };
    TylerState.career.careerB.inputs = { ...template.pathB };
    TylerState.careerNameA = template.pathA.scenarioName;
    TylerState.careerNameB = template.pathB.scenarioName;
    setActiveTemplateId(template.id);
    setHasCalculated(false);
    setActiveSection('inputs');
    updateReact();
  };

  const getCareerPathAData = (): CareerComparisonData => convertToCareerComparisonData(careerPathA);

  const getCareerPathBData = (): CareerComparisonData => convertToCareerComparisonData(careerPathB);

  // Get scenario cap based on user tier (for auto-save checks)
  const getScenarioCap = (tier: string) => {
    if (tier === 'supporter') return 1000;
    return 2; // Career Calculator allows 2 scenarios for free users
  };

  // Removed debounced autosave - Tyler wants save only on: calculate, manual save, screen change

  // Auto-save scenario after calculate (same pattern as car calculator)
  const autoSaveScenario = async (side: 'A' | 'B') => {
    const currentUser = TylerState.user;
    if (!currentUser) return;

    // Read from TylerState (single source of truth)
    const name = side === 'A' ? (TylerState.careerNameA || '').trim() : (TylerState.careerNameB || '').trim();
    const finalName = name || 'Temporary';

    debugLog('🔍 AUTO-SAVING CAREER SCENARIO', { finalName, side });

    try {
      // Get existing scenarios to check if updating
      const scenariosArray = await careerScenariosService.getCareerScenariosArray();
      const existingScenario = scenariosArray.find(s => s.name.toLowerCase() === finalName.toLowerCase());

      // Get current data for the side being saved
      const currentData = side === 'A' ? getCareerPathAData() : getCareerPathBData();

      if (existingScenario) {
        // Update existing scenario
        await careerScenariosService.updateCareerScenario(existingScenario.id, {
          name: finalName,
          description: '',
          data: currentData
        });
        debugLog('💾 SAVING CAREER SCENARIO (UPDATE)', { name: finalName });
      } else {
        // Check tier limit for new scenarios
        const cap = getScenarioCap(currentUser.tier || 'free');
        const scenarios = Array.isArray(scenariosArray) ? scenariosArray : [];
        if (scenarios.length >= cap) {
          console.log('⚠️ Scenario cap reached, not auto-saving');
          return;
        }

        // Create new scenario
        await careerScenariosService.saveCareerScenario({
          name: finalName,
          description: '',
          data: currentData
        });
        debugLog('✅ SAVING CAREER SCENARIO (NEW)', { name: finalName });
      }
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
    }
  };

  // Get max scenarios based on user tier (same as other calculators)
  const getMaxScenarios = (tier: string) => {
    if (tier === 'supporter') return 1000;
    return 2; // Career Calculator allows 2 scenarios for free users
  };

  // Only run expensive calculations after user clicks Calculate
  const emptyTotals = { beforeTax: 0, afterTax: 0, taxes: 0, benefits: 0, netEducationCost: 0, workingYears: 0, averageAnnualBeforeTax: 0, averageAnnualAfterTax: 0 };
  const results: CareerComparisonSummary = hasCalculated ? calculateComparison() : {
    pathA: emptyTotals, pathB: emptyTotals, difference: 0, winner: 'Tie', breakevenYear: 0, breakevenAge: 0, aheadFromStart: false,
    pathAName: careerPathA.scenarioName || 'Career Path A', pathBName: careerPathB.scenarioName || 'Career Path B'
  };
  const considerations = hasCalculated ? calculateConsiderations(results) : { risks: [], opportunities: [] };
  const sensitivityScenarios = hasCalculated ? calculateSensitivityAnalysis(results) : [];
  const opportunityCostData = hasCalculated && showOpportunityCost ? calculateOpportunityCost(careerPathB) : null;



  const renderSalaryGrowthTable = (career: CareerPathData, setCareer: (career: CareerPathData) => void) => {
    // Read the preview from the engine so this table can never disagree with the results.
    const salaryByYear = calculateSalaryProgression(career).map(entry => entry.salary);

    return (
      <table className="salary-growth-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr className="table-header">
            <th className="table-cell">Year</th>
            <th className="table-cell">Raise (above inflation)</th>
            <th className="table-cell">Salary That Year</th>
          </tr>
        </thead>
        <tbody>
          {/* Current Salary Row */}
          <tr className="table-row">
            <td className="table-cell">Starting Salary</td>
            <td className="table-cell">—</td>
            <td className="table-cell">
              {formatCurrency(career.hasEducation ? career.expectedStartingSalary : career.currentSalary)}
            </td>
          </tr>

          {/* Year 1-10 Rows */}
          {Array.from({ length: 10 }, (_, index) => (
            <tr key={index} className="table-row">
              <td className="table-cell">Year {index + 1}</td>
              <td className="table-cell">
                {career.salaryGrowth.type === 'percentage' ? (
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      step="0.1"
                      value={career.salaryGrowth.percentageGrowth[index]}
                      onChange={(e) => {
                        const validation = safeParseFloat(e.target.value, { min: -10, max: 50 });
                        if (validation.isValid) {
                          const newGrowth = [...career.salaryGrowth.percentageGrowth];
                          newGrowth[index] = validation.value;
                          setCareer({
                            ...career,
                            salaryGrowth: { ...career.salaryGrowth, percentageGrowth: newGrowth }
                          });
                        }
                      }}
                      placeholder="0"
                    />
                    <span>%</span>
                  </div>
                ) : career.salaryGrowth.type === 'dollar' ? (
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      value={career.salaryGrowth.dollarGrowth[index]}
                      onChange={(e) => {
                        const validation = safeParseFloat(e.target.value, { min: -100000, max: 100000 });
                        if (validation.isValid) {
                          const newGrowth = [...career.salaryGrowth.dollarGrowth];
                          newGrowth[index] = validation.value;
                          setCareer({
                            ...career,
                            salaryGrowth: { ...career.salaryGrowth, dollarGrowth: newGrowth }
                          });
                        }
                      }}
                      placeholder="0"
                    />
                    <span>$</span>
                  </div>
                ) : (
                  <span>—</span>
                )}
              </td>
              <td className="table-cell">
                {index < salaryByYear.length ? formatCurrency(salaryByYear[index]) : 'Retired'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderSalaryGrowthSection = (career: CareerPathData, setCareer: (career: CareerPathData) => void) => {
    return (
      <>
      <div className="input-card">
          <div className="card-header">
          <div className="label-with-guidance">
            <h3>Salary Growth Strategy</h3>
            <button
              className="guidance-btn"
              title="Get guidance on salary growth strategy"
              onClick={() => {
                setGuidanceModal({
                  isOpen: true,
                  title: "Salary Growth Strategy",
                  content: "Choose how your salary will grow above inflation over the first 10 years of your career. Everything is in today's dollars, so after year 10 salaries stay flat in real terms."
                });
              }}
            >
              ℹ
            </button>
          </div></div>
        
      <div className="salary-growth-section">        
        <div className="growth-options">
          <label className="radio-option">
            <input
              type="radio"
              name={`salary-growth-${career === careerPathA ? 'a' : 'b'}`}
              checked={career.salaryGrowth.type === 'no-change'}
              onChange={() => setCareer({
                ...career,
                salaryGrowth: { ...career.salaryGrowth, type: 'no-change' }
              })}
            />
            <span>No Change (keeps pace with inflation)</span>
          </label>
          
          <label className="radio-option">
            <input
              type="radio"
              name={`salary-growth-${career === careerPathA ? 'a' : 'b'}`}
              checked={career.salaryGrowth.type === 'percentage'}
              onChange={() => setCareer({
                ...career,
                salaryGrowth: { ...career.salaryGrowth, type: 'percentage' }
              })}
            />
            <span>Promotion Phase (% growth)</span>
          </label>
          
          <label className="radio-option">
            <input
              type="radio"
              name={`salary-growth-${career === careerPathA ? 'a' : 'b'}`}
              checked={career.salaryGrowth.type === 'dollar'}
              onChange={() => setCareer({
                ...career,
                salaryGrowth: { ...career.salaryGrowth, type: 'dollar' }
              })}
            />
            <span>Promotion Phase ($ growth)</span>
          </label>
        </div>

        {/* Salary Growth Table - only show if not 'no-change' */}

      </div>
              {career.salaryGrowth.type !== 'no-change' && (
                     <div className="input-card">
               <div className="growth-table-card">
                  {renderSalaryGrowthTable(career, setCareer)}
                </div></div>
              )}</div>
              </>
    );
  };

  const renderCareerPathInputs = (career: CareerPathData, setCareer: (career: CareerPathData) => void, pathName: string) => (
    <div className="career-inputs">
      {/* Education Investment Toggle - Prominent at top */}
      <div className="input-card" style={{background: '#f0fdf4', border: '2px solid #22c55e'}}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0, padding: '12px' }}>
          <input
            type="checkbox"
            checked={career.hasEducation}
            onChange={(e) => setCareer({ ...career, hasEducation: e.target.checked })}
            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
          />
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>Include Education Investment</div>
            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
              Check if this career path requires schooling (e.g., MBA, med school, college diploma)
            </div>
          </div>
        </label>
      </div>

      {/* Card 1: Scenario Details */}
      {/* Basic Information Section - Inline for now */}
      <div className="input-card">
        <div className="card-header">
          <h3>Basic Information</h3>
        </div>
        <div className="input-row">
          <div className="form-group">
                        <label title={isUSA ? "Your state affects tax calculations" : "Your province affects tax calculations"}>{isUSA ? 'State' : 'Province'}</label>
            <select
              value={career.province}
              onChange={(e) => setCareer({ ...career, province: e.target.value })}
              title={isUSA ? "Your state affects tax calculations" : "Your province affects tax calculations"}
            >
              {isUSA ? (
                <>
                  {Object.entries(US_STATE_TAX_RATES).map(([code, info]) => (
                    <option key={code} value={code}>{info.name}</option>
                  ))}
                </>
              ) : (
                <>
                  <option value="Alberta">Alberta</option>
                  <option value="British Columbia">British Columbia</option>
                  <option value="Manitoba">Manitoba</option>
                  <option value="New Brunswick">New Brunswick</option>
                  <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
                  <option value="Northwest Territories">Northwest Territories</option>
                  <option value="Nova Scotia">Nova Scotia</option>
                  <option value="Nunavut">Nunavut</option>
                  <option value="Ontario">Ontario</option>
                  <option value="Prince Edward Island">Prince Edward Island</option>
                  <option value="Quebec">Quebec</option>
                  <option value="Saskatchewan">Saskatchewan</option>
                  <option value="Yukon">Yukon</option>
                </>
              )}
            </select> 
            <label title="How old are you now?">Current Age</label>
            <input
              type="number"
              value={career.currentAge}
              onChange={(e) => setCareer({ ...career, currentAge: Number(e.target.value) })}
              min="16"
              max="80"
              title="How old are you now?"
            />
            <label title="When do you plan to retire? (typically 65)">Retirement Age</label>
            <input
              type="number"
              value={career.retirementAge}
              onChange={(e) => setCareer({ ...career, retirementAge: Number(e.target.value) })}
              min="50"
              max="80"
              title="When do you plan to retire? (typically 65)"
            />

            <label title="Your total annual salary before taxes. Find typical salaries at jobbank.gc.ca, glassdoor.ca, or payscale.com">Current Salary</label>
            <input
              type="number"
              value={career.currentSalary}
              onChange={(e) => setCareer({ ...career, currentSalary: Number(e.target.value) })}
              min="20000"
              max="500000"
              step="1000"
              title="Your total annual salary before taxes. Find typical salaries at jobbank.gc.ca, glassdoor.ca, or payscale.com"
            />
            <label title="Annual after-tax value of benefits: RRSP match, health insurance, HSA. It is not taxed again. Use the breakdown below if part of it is a taxable bonus.">Annual Benefits Value</label>
            <input
              type="number"
              value={career.nonSalaryBenefits}
              onChange={(e) => setCareer({ ...career, nonSalaryBenefits: Number(e.target.value) })}
              min="0"
              max="50000"
              step="500"
              title="Total annual cash value of benefits. RRSP match, health insurance, HSA, bonuses. See breakdown below for help."
              style={{ marginBottom: '8px' }}
            />

             </div>
                         {/* Benefits Calculator - Only in Advanced Mode */}
            {!startSimpleMode && (
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '8px'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    const showBreakdown = !career.benefitsBreakdown;
                    if (showBreakdown && !career.benefitsBreakdown) {
                      // Initialize breakdown on first open
                      setCareer({
                        ...career,
                        benefitsBreakdown: {
                          rrspMatchPercent: 0,
                          bonusPercent: 0,
                          healthInsurance: 0,
                          hsaAmount: 0,
                          profDevBudget: 0,
                          otherFixed: 0
                        }
                      });
                    } else {
                      // Clear breakdown when closing
                      setCareer({
                        ...career,
                        benefitsBreakdown: undefined
                      });
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    padding: '4px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {career.benefitsBreakdown ? '▼' : '▶'} Calculate My Benefits Breakdown
                </button>

                {career.benefitsBreakdown && (
                  <div style={{
                    marginTop: '12px',
                    display: 'grid',
                    gap: '12px'
                  }}>
                    <div style={{
                      fontSize: '0.85rem',
                      color: '#6b7280',
                      padding: '8px',
                      background: '#eff6ff',
                      borderRadius: '6px',
                      borderLeft: '3px solid #3b82f6'
                    }}>
                      <strong>💡 Why break it down?</strong> RRSP matching and bonuses grow with your salary; fixed benefits hold their value in today's dollars. Only the bonus is taxed as income.
                    </div>

                    {/* Salary-Linked Benefits */}
                    <div style={{
                      background: 'white',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '8px', color: '#374151', fontSize: '0.95rem' }}>
                        📈 Grows with Salary
                      </div>

                      <div style={{ display: 'grid', gap: '8px' }}>
                        <div>
                          <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                            RRSP Match (% of salary)
                            <span style={{ color: '#3b82f6', marginLeft: '4px', cursor: 'help' }} title="If your employer matches 4% and you make $100k, that's $4k/year. As salary grows to $150k, match grows to $6k.">ⓘ</span>
                          </label>
                          <input
                            type="number"
                            value={career.benefitsBreakdown.rrspMatchPercent}
                            onChange={(e) => {
                              const breakdown = career.benefitsBreakdown!;
                              setCareer({
                                ...career,
                                benefitsBreakdown: { ...breakdown, rrspMatchPercent: Number(e.target.value) }
                              });
                            }}
                            min="0"
                            max="20"
                            step="0.5"
                            placeholder="e.g., 4"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                            Annual Bonus (% of salary)
                            <span style={{ color: '#3b82f6', marginLeft: '4px', cursor: 'help' }} title="If you get a 10% annual bonus on $100k, that's $10k/year. As salary grows, bonus grows too.">ⓘ</span>
                          </label>
                          <input
                            type="number"
                            value={career.benefitsBreakdown.bonusPercent}
                            onChange={(e) => {
                              const breakdown = career.benefitsBreakdown!;
                              setCareer({
                                ...career,
                                benefitsBreakdown: { ...breakdown, bonusPercent: Number(e.target.value) }
                              });
                            }}
                            min="0"
                            max="100"
                            step="5"
                            placeholder="e.g., 10"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Fixed Benefits */}
                    <div style={{
                      background: 'white',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '8px', color: '#374151', fontSize: '0.95rem' }}>
                        📊 Fixed Amount (today's dollars)
                      </div>

                      <div style={{ display: 'grid', gap: '8px' }}>
                        <div>
                          <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                            Health Insurance (annual value)
                            <span style={{ color: '#3b82f6', marginLeft: '4px', cursor: 'help' }} title="How much your employer pays for your health insurance premium per year. Typically $500/month = $6,000/year.">ⓘ</span>
                          </label>
                          <input
                            type="number"
                            value={career.benefitsBreakdown.healthInsurance}
                            onChange={(e) => {
                              const breakdown = career.benefitsBreakdown!;
                              setCareer({
                                ...career,
                                benefitsBreakdown: { ...breakdown, healthInsurance: Number(e.target.value) }
                              });
                            }}
                            min="0"
                            max="20000"
                            step="500"
                            placeholder="e.g., 6000"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                            Health Spending Account (HSA)
                            <span style={{ color: '#3b82f6', marginLeft: '4px', cursor: 'help' }} title="Annual HSA allocation for medical expenses. Typically $1,000-$3,000/year.">ⓘ</span>
                          </label>
                          <input
                            type="number"
                            value={career.benefitsBreakdown.hsaAmount}
                            onChange={(e) => {
                              const breakdown = career.benefitsBreakdown!;
                              setCareer({
                                ...career,
                                benefitsBreakdown: { ...breakdown, hsaAmount: Number(e.target.value) }
                              });
                            }}
                            min="0"
                            max="10000"
                            step="500"
                            placeholder="e.g., 2000"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                            Professional Development Budget
                            <span style={{ color: '#3b82f6', marginLeft: '4px', cursor: 'help' }} title="Annual budget for courses, conferences, certifications. Typically $1,000-$5,000/year.">ⓘ</span>
                          </label>
                          <input
                            type="number"
                            value={career.benefitsBreakdown.profDevBudget}
                            onChange={(e) => {
                              const breakdown = career.benefitsBreakdown!;
                              setCareer({
                                ...career,
                                benefitsBreakdown: { ...breakdown, profDevBudget: Number(e.target.value) }
                              });
                            }}
                            min="0"
                            max="20000"
                            step="500"
                            placeholder="e.g., 2000"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                          />
                        </div>

                        <div>
                          <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                            Other Fixed Benefits
                            <span style={{ color: '#3b82f6', marginLeft: '4px', cursor: 'help' }} title="Stock options, parking, gym membership, etc. Enter total annual value.">ⓘ</span>
                          </label>
                          <input
                            type="number"
                            value={career.benefitsBreakdown.otherFixed}
                            onChange={(e) => {
                              const breakdown = career.benefitsBreakdown!;
                              setCareer({
                                ...career,
                                benefitsBreakdown: { ...breakdown, otherFixed: Number(e.target.value) }
                              });
                            }}
                            min="0"
                            max="50000"
                            step="500"
                            placeholder="e.g., 1000"
                            style={{ width: '100%', padding: '6px 8px', fontSize: '0.9rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Total Calculation */}
                    <div style={{
                      background: '#f0fdf4',
                      border: '2px solid #22c55e',
                      borderRadius: '6px',
                      padding: '12px'
                    }}>
                      <div style={{ fontWeight: '600', color: '#15803d', marginBottom: '4px' }}>
                        Total Benefits (Year 1)
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#166534' }}>
                        ${(() => {
                          const salary = career.hasEducation ? career.expectedStartingSalary : career.currentSalary;
                          const rrsp = salary * (career.benefitsBreakdown.rrspMatchPercent / 100);
                          const bonus = salary * (career.benefitsBreakdown.bonusPercent / 100);
                          const fixed = career.benefitsBreakdown.healthInsurance +
                                       career.benefitsBreakdown.hsaAmount +
                                       career.benefitsBreakdown.profDevBudget +
                                       career.benefitsBreakdown.otherFixed;
                          return (rrsp + bonus + fixed).toLocaleString('en-CA', { maximumFractionDigits: 0 });
                        })()}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const salary = career.hasEducation ? career.expectedStartingSalary : career.currentSalary;
                          const rrsp = salary * (career.benefitsBreakdown!.rrspMatchPercent / 100);
                          const bonus = salary * (career.benefitsBreakdown!.bonusPercent / 100);
                          const fixed = career.benefitsBreakdown!.healthInsurance +
                                       career.benefitsBreakdown!.hsaAmount +
                                       career.benefitsBreakdown!.profDevBudget +
                                       career.benefitsBreakdown!.otherFixed;
                          const total = rrsp + bonus + fixed;
                          setCareer({ ...career, nonSalaryBenefits: total });
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '8px 16px',
                          background: '#22c55e',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        ✓ Use This Value
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
             
             </div>        
      </div>

      {/* Salary Growth Strategy Section */}
      {renderSalaryGrowthSection(career, setCareer)}

      {/* Education Investment Section */}
      {career.hasEducation && (
        <div className="input-card">
          <div className="card-header">
            <h3>Education Investment</h3>
          </div>
          <div className="input-row">
            <div className="form-group">
              <label>Years in School</label>
              <input
                type="number"
                value={career.yearsInSchool}
                onChange={(e) => setCareer({ ...career, yearsInSchool: Number(e.target.value) })}
                min="1"
                max="8"
              />
              <label>Annual School Cost</label>
              <input
                type="number"
                value={career.annualSchoolCost}
                onChange={(e) => setCareer({ ...career, annualSchoolCost: Number(e.target.value) })}
                min="5000"
                max="100000"
                step="1000"
              />
              <label>Expected Starting Salary (After Education)</label>
              <input
                type="number"
                value={career.expectedStartingSalary}
                onChange={(e) => setCareer({ ...career, expectedStartingSalary: Number(e.target.value) })}
                min="30000"
                max="200000"
                step="1000"
              />
            </div>
          </div>
          <div className="input-row">
            <div className="form-group">
              <label title="Part-time or summer work income while in school (typically $0-$15,000)">Income During School (Annual)</label>
              <input
                type="number"
                value={career.incomeDuringSchool}
                onChange={(e) => setCareer({ ...career, incomeDuringSchool: Number(e.target.value) })}
                min="0"
                max="40000"
                step="1000"
                title="Part-time or summer work income while in school (typically $0-$15,000)"
              />
              <label title="Job search time after graduation. Most programs: 0-3 months.">Months After School to Start Job</label>
              <input
                type="number"
                value={career.monthsAfterSchoolToStartJob}
                onChange={(e) => setCareer({ ...career, monthsAfterSchoolToStartJob: Number(e.target.value) })}
                min="0"
                max="12"
                title="Job search time after graduation. Most programs: 0-3 months."
              />
            </div>
          </div>

          {/* Validation Warnings (non-blocking) */}
          {career.yearsInSchool === 0 && (
            <div style={{ padding: '0.75rem', backgroundColor: '#fef3c7', borderRadius: '6px', marginTop: '0.75rem', border: '1px solid #f59e0b' }}>
              <span style={{ color: '#92400e', fontSize: '0.875rem' }}>
                ⚠️ Years in School is 0 - no education costs will be calculated
              </span>
            </div>
          )}
          {career.currentAge + career.yearsInSchool >= career.retirementAge && (
            <div style={{ padding: '0.75rem', backgroundColor: '#fee2e2', borderRadius: '6px', marginTop: '0.75rem', border: '1px solid #ef4444' }}>
              <span style={{ color: '#b91c1c', fontSize: '0.875rem' }}>
                ⚠️ Education ends at age {career.currentAge + career.yearsInSchool}, but retirement is at {career.retirementAge}. Very few working years remaining.
              </span>
            </div>
          )}
          {career.expectedStartingSalary < career.currentSalary * 0.8 && (
            <div style={{ padding: '0.75rem', backgroundColor: '#fef3c7', borderRadius: '6px', marginTop: '0.75rem', border: '1px solid #f59e0b' }}>
              <span style={{ color: '#92400e', fontSize: '0.875rem' }}>
                ⚠️ Expected salary after education (${career.expectedStartingSalary.toLocaleString()}) is lower than current (${career.currentSalary.toLocaleString()}). Is this intentional?
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Define sections (following Car Calculator pattern)
  const sections = [
    {
      id: 'inputs',
      label: 'Career Inputs',
      render: () => (
        <div className="comparison-layout" style={{"maxWidth":"90vw"}}>
          {/* Ready-made comparisons */}
          <div className="career-templates">
            <h3>Start From an Example</h3>
            <p className="template-intro">
              Each example fills both paths with researched numbers. Load one, then change anything to match your situation.
            </p>
            <div className="template-cards">
              {getCareerTemplatesForCountry(isUSA).map(template => (
                <button
                  key={template.id}
                  type="button"
                  className={`template-card${activeTemplateId === template.id ? ' template-card-active' : ''}`}
                  onClick={() => handleLoadTemplate(template)}
                >
                  <strong>{template.name}</strong>
                  <span className="template-question">{template.question}</span>
                  <span className="template-source">{template.outOfPocket}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Career Path A Section */}
          <ErrorBoundary context="Career Path A inputs">
            <div className="career-path-section">
              <div className="career-path-header">
                <h2>Career Path A</h2>
                <div className="path-indicator path-a">A</div>
              </div>
              <ScenariosTab
                calculatorType="career"
                user={TylerState.user}
                onLoadScenarioToA={handleLoadScenarioToA}
                currentData={getCareerPathAData()}
              />
              {renderCareerPathInputs(careerPathA, setCareerPathA, "A")}
            </div>
          </ErrorBoundary>

          {/* Career Path B Section */}
          <ErrorBoundary context="Career Path B inputs">
            <div className="career-path-section">
              <div className="career-path-header">
                <h2>Career Path B</h2>
                <div className="path-indicator path-b">B</div>
              </div>
              <ScenariosTab
                calculatorType="career"
                user={TylerState.user}
                onLoadScenarioToA={handleLoadScenarioToB}
                currentData={getCareerPathBData()}
              />
              {renderCareerPathInputs(careerPathB, setCareerPathB, "B")}
            </div>
          </ErrorBoundary>

          {/* Calculate Button */}
          <div className="calculate-section">
            {careerPathA.currentAge !== careerPathB.currentAge && (
              <div style={{ padding: '0.75rem', backgroundColor: '#fef3c7', borderRadius: '6px', marginBottom: '0.75rem', border: '1px solid #f59e0b' }}>
                <span style={{ color: '#92400e', fontSize: '0.875rem' }}>
                  ⚠️ Path A starts at age {careerPathA.currentAge} and Path B at age {careerPathB.currentAge}. Both paths describe you, so they normally share your current age. The break-even compares the two paths age by age.
                </span>
              </div>
            )}
            <button
              className="calculate-button"
              onClick={() => {
                if (disclaimerAccepted) {
                  // Skip disclaimer on subsequent calcs, still roll ads
                  const currentUser = TylerState.user;
                  const isSupporter = currentUser && currentUser.tier === 'supporter';
                  const showAd = !isSupporter && (!currentUser || Math.random() < 0.2);
                  if (showAd) {
                    setShowAdModal(true);
                  } else {
                    performCalculation();
                  }
                  return;
                }
                setShowDisclaimerModal(true);
              }}
            >
              Calculate Career Comparison
            </button>

            {/* Advanced Options - Only in Advanced Mode */}
            {!startSimpleMode && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                  Advanced Analysis Options
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showOpportunityCost}
                    onChange={(e) => setShowOpportunityCost(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.9rem' }}>
                    Show Investment Opportunity Cost Analysis
                    <span style={{ color: '#6b7280', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                      (What if you invested foregone salary?)
                    </span>
                  </span>
                </label>
                {showOpportunityCost && (
                  <div style={{ marginTop: '0.75rem', marginLeft: '1.75rem' }}>
                    <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                      Expected Investment Return Rate (%)
                    </label>
                    <input
                      type="number"
                      value={investmentReturnRate}
                      onChange={(e) => setInvestmentReturnRate(Number(e.target.value))}
                      min="0"
                      max="15"
                      step="0.5"
                      style={{
                        width: '100px',
                        padding: '6px 8px',
                        fontSize: '0.9rem',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db'
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )
    }
  ];

  // Results sections - extracted from monolithic output block
  const resultsSection = hasCalculated ? [
    // Section 1: Summary & Winner Banner
    {
      id: 'summary',
      label: 'Summary',
      render: () => (
        <ErrorBoundary context="Career comparison results summary">
          <div className="comparison-results">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Career Path Comparison</h2>
            </div>

            {/* Big Winner Banner */}
            {(() => {
              const schoolingCost = Math.max(results.pathA.netEducationCost, results.pathB.netEducationCost);
              const isTie = results.winner === 'Tie';
              const winnerName = results.winner === 'B' ? results.pathBName : results.pathAName;
              const lifetimeDiff = Math.abs(results.difference);
              const isPathBBetter = results.winner === 'B';
              const accentColor = isPathBBetter ? '#059669' : '#dc2626';
              const breakEvenText = results.aheadFromStart
                ? `${results.pathBName} is ahead from the first year`
                : results.breakevenYear === 0
                  ? `${results.pathAName} stays ahead for the whole career`
                  : isPathBBetter
                    ? `Break-even at age ${results.breakevenAge} (year ${results.breakevenYear}) - then ${results.pathBName} pulls ahead`
                    : `${results.pathBName} catches up at age ${results.breakevenAge} but ${results.pathAName} finishes ahead`;

              return (
                <div style={{
                  backgroundColor: isPathBBetter ? '#f0fdf4' : '#fef2f2',
                  border: `3px solid ${accentColor}`,
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '2rem',
                  textAlign: 'center'
                }}>
                  <h2 style={{
                    color: accentColor,
                    margin: '0 0 0.75rem 0',
                    fontSize: '1.75rem',
                    fontWeight: 'bold'
                  }}>
                    {isTie ? "🤝 It's a Tie" : `🏆 ${winnerName} Wins!`}
                  </h2>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Lifetime earnings difference: ${formatInThousands(lifetimeDiff)}
                  </div>
                  {!isTie && (
                    <div style={{ fontSize: '1rem', color: accentColor, marginTop: '0.5rem' }}>
                      {breakEvenText}
                    </div>
                  )}
                  {schoolingCost > 0 && (
                    <div style={{ fontSize: '1rem', marginTop: '0.5rem' }}>
                      Out of pocket for school: {formatCurrency(schoolingCost)}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Summary Cards with Icons */}
            <div className="comparison-grid" style={{ marginBottom: '2rem' }}>
              <div className="scenario-card" style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '2px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '3rem', textAlign: 'center', marginBottom: '1rem' }}>
                  💼
                </div>
                <h3 style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {results.pathAName}
                </h3>
                <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                  {careerPathA.hasEducation ? 'Includes education period' : 'Continue current path'}
                </p>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  color: '#111827',
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px'
                }}>
                  ${formatInThousands(results.pathA.afterTax)}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', textAlign: 'center', marginBottom: '1.5rem' }}>
                  Total Lifetime Earnings (After Tax)
                </div>
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Avg Annual:</span>
                    <span style={{ fontWeight: '600' }}>${formatInThousands(results.pathA.averageAnnualAfterTax)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Before Tax:</span>
                    <span style={{ fontWeight: '600' }}>${formatInThousands(results.pathA.beforeTax)}</span>
                  </div>
                </div>
              </div>

              <div className="scenario-card" style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: results.difference >= 0 ? '2px solid #22c55e' : '2px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '3rem', textAlign: 'center', marginBottom: '1rem' }}>
                  {careerPathB.hasEducation ? '🎓' : '💼'}
                </div>
                <h3 style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {results.pathBName}
                </h3>
                <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                  {careerPathB.hasEducation ? 'Includes education period' : 'Continue current path'}
                </p>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  color: results.difference >= 0 ? '#16a34a' : '#111827',
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: results.difference >= 0 ? '#f0fdf4' : '#f9fafb',
                  borderRadius: '8px'
                }}>
                  ${formatInThousands(results.pathB.afterTax)}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', textAlign: 'center', marginBottom: '1.5rem' }}>
                  Total Lifetime Earnings (After Tax)
                </div>
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Avg Annual:</span>
                    <span style={{ fontWeight: '600' }}>${formatInThousands(results.pathB.averageAnnualAfterTax)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Before Tax:</span>
                    <span style={{ fontWeight: '600' }}>${formatInThousands(results.pathB.beforeTax)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics Highlights */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💰</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  ${formatInThousands(Math.abs(results.difference))}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Lifetime Difference
                </div>
              </div>

              {results.breakevenYear > 0 && (
                <div style={{
                  backgroundColor: 'white',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏱️</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    Age {results.breakevenAge}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Break-Even Point (year {results.breakevenYear})
                  </div>
                </div>
              )}

              <div style={{
                backgroundColor: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  {((Math.abs(results.difference) / Math.max(results.pathA.afterTax, results.pathB.afterTax)) * 100).toFixed(1)}%
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Earnings Difference
                </div>
              </div>
            </div>
          </div>
        </ErrorBoundary>
      )
    },
    // Section 2: Tax Impact Analysis
    {
      id: 'tax-impact',
      label: 'Tax Impact',
      render: () => (
        <div className="tax-analysis">
          <h3>Tax Impact Analysis</h3>
          <div className="tax-grid">
            <div className="tax-card">
              <h4>Path A Tax Impact</h4>
              <div className="tax-metrics">
                <div className="tax-metric">
                  <span>Total Tax Paid:</span>
                  <span>{formatCurrencyWithColor(results.pathA.beforeTax - results.pathA.afterTax, 'negative')}</span>
                </div>
                <div className="tax-metric">
                  <span>Effective Tax Rate:</span>
                  <span>{(((results.pathA.beforeTax - results.pathA.afterTax) / results.pathA.beforeTax) * 100).toFixed(1)}%</span>
                </div>
                <div className="tax-metric">
                  <span>Province:</span>
                  <span>{careerPathA.province}</span>
                </div>
              </div>
            </div>

            <div className="tax-card">
              <h4>Path B Tax Impact</h4>
              <div className="tax-metrics">
                <div className="tax-metric">
                  <span>Total Tax Paid:</span>
                  <span>{formatCurrencyWithColor(results.pathB.beforeTax - results.pathB.afterTax, 'negative')}</span>
                </div>
                <div className="tax-metric">
                  <span>Effective Tax Rate:</span>
                  <span>{(((results.pathB.beforeTax - results.pathB.afterTax) / results.pathB.beforeTax) * 100).toFixed(1)}%</span>
                </div>
                <div className="tax-metric">
                  <span>Province:</span>
                  <span>{careerPathB.province}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Section 3: Key Considerations (Risks & Opportunities)
    {
      id: 'considerations',
      label: 'Risks & Opportunities',
      render: () => (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Key Considerations</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {/* Risks Column */}
            <div>
              <h4 style={{ color: '#dc2626', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🔴 Top Risks
              </h4>
              {considerations.risks.map((risk, index) => (
                <div key={index} style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>{risk.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#991b1b' }}>
                        {risk.title}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#7f1d1d' }}>
                        {risk.description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Opportunities Column */}
            <div>
              <h4 style={{ color: '#059669', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🟢 Top Opportunities
              </h4>
              {considerations.opportunities.length > 0 ? considerations.opportunities.map((opp, index) => (
                <div key={index} style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>{opp.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#065f46' }}>
                        {opp.title}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#064e3b' }}>
                        {opp.description}
                      </div>
                    </div>
                  </div>
                </div>
              )) : (
                <div style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1rem',
                  color: '#6b7280',
                  fontSize: '0.875rem'
                }}>
                  No significant opportunities identified for this comparison. Try adjusting salary growth rates or enabling education investment to see potential advantages.
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    // Section 4: Sensitivity Analysis
    {
      id: 'sensitivity',
      label: 'Sensitivity Analysis',
      render: () => sensitivityScenarios.length > 0 ? (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Sensitivity Analysis</h3>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
            How does break-even age change if your salary growth assumptions are wrong?
          </p>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Scenario</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Break-Even Year</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Break-Even Age</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Lifetime Difference</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Impact</th>
                </tr>
              </thead>
              <tbody>
                {sensitivityScenarios.map((scenario, index) => (
                  <tr key={index} style={{
                    backgroundColor: scenario.multiplier === 1.0 ? '#eff6ff' : 'white',
                    fontWeight: scenario.multiplier === 1.0 ? '600' : 'normal'
                  }}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                      {scenario.label}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                      {scenario.breakevenYear > 0 ? `${scenario.breakevenYear} years` : 'N/A'}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                      {scenario.breakevenAge > 0 ? `Age ${scenario.breakevenAge}` : 'N/A'}
                    </td>
                    <td style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      borderBottom: '1px solid #e5e7eb',
                      color: scenario.lifetimeDifference >= 0 ? '#059669' : '#dc2626'
                    }}>
                      ${formatInThousands(Math.abs(scenario.lifetimeDifference))}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                      {scenario.impact}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null
    },
    // Section 5: Investment Opportunity Cost
    {
      id: 'opportunity-cost',
      label: 'Investment Cost',
      render: () => (opportunityCostData && opportunityCostData.totalForegoneSalary > 0) ? (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Investment Opportunity Cost</h3>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
            What if you invested the foregone salary instead of going back to school?
          </p>
          <div style={{
            backgroundColor: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '8px',
            padding: '1.5rem'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.25rem' }}>
                  Foregone Salary
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#78350f' }}>
                  ${formatInThousands(opportunityCostData.totalForegoneSalary)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.25rem' }}>
                  If Invested at {opportunityCostData.returnRate}%
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#78350f' }}>
                  ${formatInThousands(opportunityCostData.futureValueIfInvested)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#92400e' }}>
                  at age {careerPathB.retirementAge}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.25rem' }}>
                  Opportunity Cost
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#78350f' }}>
                  ${formatInThousands(opportunityCostData.opportunityCost)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#92400e' }}>
                  potential growth
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Investment Opportunity Cost</h3>
          <div style={{
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '2rem',
            color: '#6b7280'
          }}>
            <p style={{ margin: 0, fontSize: '0.95rem' }}>
              Enable "Include Education Investment" on either career path to see the opportunity cost of foregone salary during school.
            </p>
          </div>
        </div>
      )
    },
    // Section 6: Detailed Cash Flow Analysis
    {
      id: 'cash-flow',
      label: 'Cash Flow Details',
      render: () => (
        <div className="cash-flow-analysis">
          <div style={{
            background: '#fef3c7',
            border: '2px solid #f59e0b',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{ fontSize: '2rem' }}>📊</div>
            <div>
              <h4 style={{ margin: '0 0 4px 0', color: '#92400e', fontSize: '1.1rem' }}>Year-by-Year Cash Flow Breakdown</h4>
              <p style={{ margin: 0, color: '#78350f', fontSize: '0.95rem' }}>
                See exactly when you'll be in the "pain valley" (school years with low income) and when earnings accelerate.
                Opening/Closing balance shows cumulative net worth over time.
              </p>
            </div>
          </div>
          <h3>Detailed Cash Flow Analysis</h3>

          <div className="cash-flow-grid">
            <div className="cash-flow-section">
              <h4>{results.pathAName}</h4>
              <div className="career-cash-flow-table">
                <div className="cash-flow-header">
                  <div className="cash-flow-cell">Year</div>
                  <div className="cash-flow-cell">Age</div>
                  <div className="cash-flow-cell">Period</div>
                  <div className="cash-flow-cell">Opening</div>
                  <div className="cash-flow-cell">Gross Earnings</div>
                  <div className="cash-flow-cell">Taxes</div>
                  <div className="cash-flow-cell">Education</div>
                  <div className="cash-flow-cell">Net Income</div>
                  <div className="cash-flow-cell">Closing</div>
                </div>
                {calculateDetailedCashFlows(careerPathA).map((flow, index) => (
                  <div key={index} className="cash-flow-row">
                    <div className="cash-flow-cell">{flow.year}</div>
                    <div className="cash-flow-cell">{flow.age}</div>
                    <div className="cash-flow-cell">{flow.period}</div>
                    <div className="cash-flow-cell">{formatCurrency(flow.openingBalance)}</div>
                    <div className="cash-flow-cell">{formatCurrencyWithColor(flow.grossEarnings, 'positive')}</div>
                    <div className="cash-flow-cell">{formatCurrencyWithColor(flow.taxes, 'negative')}</div>
                    <div className="cash-flow-cell">{flow.educationCosts > 0 ? formatCurrencyWithColor(flow.educationCosts, 'negative') : formatCurrency(0)}</div>
                    <div className="cash-flow-cell">{formatCurrencyWithColor(flow.netIncome, flow.netIncome >= 0 ? 'positive' : 'negative')}</div>
                    <div className="cash-flow-cell">{formatCurrency(flow.closingBalance)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="cash-flow-section">
              <h4>{results.pathBName}</h4>
              <div className="career-cash-flow-table">
                <div className="cash-flow-header">
                  <div className="cash-flow-cell">Year</div>
                  <div className="cash-flow-cell">Age</div>
                  <div className="cash-flow-cell">Period</div>
                  <div className="cash-flow-cell">Opening</div>
                  <div className="cash-flow-cell">Gross Earnings</div>
                  <div className="cash-flow-cell">Taxes</div>
                  <div className="cash-flow-cell">Education</div>
                  <div className="cash-flow-cell">Net Income</div>
                  <div className="cash-flow-cell">Closing</div>
                </div>
                {calculateDetailedCashFlows(careerPathB).map((flow, index) => (
                  <div key={index} className="cash-flow-row">
                    <div className="cash-flow-cell">{flow.year}</div>
                    <div className="cash-flow-cell">{flow.age}</div>
                    <div className="cash-flow-cell">{flow.period}</div>
                    <div className="cash-flow-cell">{formatCurrency(flow.openingBalance)}</div>
                    <div className="cash-flow-cell">{formatCurrencyWithColor(flow.grossEarnings, 'positive')}</div>
                    <div className="cash-flow-cell">{formatCurrencyWithColor(flow.taxes, 'negative')}</div>
                    <div className="cash-flow-cell">{flow.educationCosts > 0 ? formatCurrencyWithColor(flow.educationCosts, 'negative') : formatCurrency(0)}</div>
                    <div className="cash-flow-cell">{formatCurrencyWithColor(flow.netIncome, flow.netIncome >= 0 ? 'positive' : 'negative')}</div>
                    <div className="cash-flow-cell">{formatCurrency(flow.closingBalance)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Section 7: Break Even Analysis Chart
    {
      id: 'break-even-chart',
      label: 'Break Even Chart',
      render: () => (
        <div className="break-even-analysis">
          <h3>Break Even Analysis</h3>
          <p>
            This chart shows the cumulative net income over time for both career paths and identifies when they cross.
            <span style={{ color: '#3b82f6', marginLeft: '8px', cursor: 'help' }} title="The x-axis shows your age (not years from now). For example, 'Age 30' means when you are 30 years old, regardless of which career path you choose.">ⓘ X-axis shows age</span>
          </p>

          <div className="break-even-chart">
            <div className="chart-container">
              <div className="chart-header">
                <div className="chart-legend">
                  <div className="legend-item">
                    <div className="legend-color path-a"></div>
                    <span>{results.pathAName}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color path-b"></div>
                    <span>{results.pathBName}</span>
                  </div>
                </div>
              </div>

              <div className="chart-content">
                {(() => {
                  const pathAData = calculateCumulativeNetIncome(careerPathA);
                  const pathBData = calculateCumulativeNetIncome(careerPathB);
                  const breakEvenPoint = findBreakEvenPoint();
                  const maxYears = Math.max(pathAData.length, pathBData.length);
                  const minValue = Math.min(
                    0,  // Ensure chart starts at 0
                    ...pathAData.map(d => d.cumulativeNetIncome),
                    ...pathBData.map(d => d.cumulativeNetIncome)
                  );
                  const maxValue = Math.max(
                    ...pathAData.map(d => d.cumulativeNetIncome),
                    ...pathBData.map(d => d.cumulativeNetIncome)
                  );
                  const valueRange = maxValue - minValue;
                  const startingAge = Math.min(careerPathA.currentAge, careerPathB.currentAge);

                  return (
                    <div className="chart-grid">
                      {/* Y-axis labels */}
                      <div className="y-axis">
                        {[1, 0.75, 0.5, 0.25, 0].map(ratio => (
                          <div key={ratio} className="y-label">
                            ${formatInThousands(minValue + (valueRange * ratio))}
                          </div>
                        ))}
                      </div>

                      {/* Chart area */}
                      <div className="chart-area">
                        {/* Path A line */}
                        <svg className="chart-svg" viewBox={`0 0 ${maxYears} 100`} preserveAspectRatio="none">
                          <polyline
                            className="path-a-line"
                            points={pathAData.map((d, i) =>
                              `${i},${100 - ((d.cumulativeNetIncome - minValue) / valueRange * 100)}`
                            ).join(' ')}
                          />
                          <polyline
                            className="path-b-line"
                            points={pathBData.map((d, i) =>
                              `${i},${100 - ((d.cumulativeNetIncome - minValue) / valueRange * 100)}`
                            ).join(' ')}
                          />
                          {breakEvenPoint && (
                            <circle
                              className="break-even-marker"
                              cx={breakEvenPoint.year}
                              cy={100 - ((breakEvenPoint.pathBCumulative - minValue) / valueRange * 100)}
                              r="2"
                            />
                          )}
                        </svg>

                        {/* X-axis labels - show age instead of year */}
                        <div className="x-axis">
                          {(() => {
                            const step = maxYears > 25 ? 10 : 5;
                            return Array.from({ length: Math.ceil(maxYears / step) + 1 }).map((_, i) => {
                              const year = i * step;
                              const age = startingAge + year;
                              return year <= maxYears ? (
                                <div key={i} className="x-label">
                                  Age {age}
                                </div>
                              ) : null;
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Break even summary */}
            {(() => {
              const breakEvenPoint = findBreakEvenPoint();
              return breakEvenPoint ? (
                <div className="break-even-summary">
                  <h4>Break Even Analysis</h4>
                  <div className="break-even-details">
                    <div className="break-even-detail">
                      <span className="label">Break Even Year:</span>
                      <span className="value">Year {breakEvenPoint.year}</span>
                    </div>
                    <div className="break-even-detail">
                      <span className="label">Age at Break Even:</span>
                      <span className="value">Age {breakEvenPoint.age}</span>
                    </div>
                    <div className="break-even-detail">
                      <span className="label">Cumulative Net Income at Break Even:</span>
                      <span className="value">${formatInThousands(breakEvenPoint.pathBCumulative)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="break-even-summary">
                  <h4>No Break Even Point</h4>
                  <p>Path A maintains a higher cumulative net income throughout the career timeline.</p>
                </div>
              );
            })()}
          </div>
        </div>
      )
    },
    // Section 8: Export to PDF
    {
      id: 'export-pdf',
      label: 'Export to PDF',
      render: () => (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h3>Generate Professional Report</h3>
          <p>Create a comprehensive PDF report of your career comparison analysis.</p>
          <button
            onClick={handleExportPDF}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📄 Export to PDF
          </button>
        </div>
      )
    }
  ] : [];

  // Combine input sections with results sections
  const allSections = [...sections, ...resultsSection];

  // Section navigation handler (Car Calculator pattern)
  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Dynamic Next Button Component (Car Calculator pattern)
  const NextButton = () => {
    const currentIndex = allSections.findIndex(s => s.id === activeSection);
    const nextSection = currentIndex < allSections.length - 1 ? allSections[currentIndex + 1] : null;

    // If we're on inputs and haven't calculated yet, show Calculate button (handled in inputs section)
    // If there's a next section (output sections), show Next button
    if (nextSection && hasCalculated) {
      return (
        <div className="dynamic-next-button">
          <button
            onClick={() => handleSectionClick(nextSection.id)}
            className="run-simulation-btn primary"
          >
            Next: {nextSection.label} →
          </button>
        </div>
      );
    }

    return null;
  };

  // FAQ data based on country - Enhanced for SEO competitiveness
  const canadaFAQs = [
    { question: "Should I go back to school or stay in my current career in Canada?", answer: "It depends on the earning potential of your new career versus the cost and time of education. Our calculator compares your current career trajectory against getting additional education, accounting for Canadian tuition costs, OSAP, lost income during school, CPP/EI contributions, and lifetime after-tax earnings to show which path maximizes your financial future." },
    { question: "How do I calculate the ROI of going back to school in Canada?", answer: "Calculate total education costs (tuition + lost income) versus the salary increase in your new career over your working lifetime. Our calculator factors in Canadian provincial taxes, CPP/EI deductions, RRSP room accumulation, and the opportunity cost of years spent in school to give you a clear after-tax financial comparison." },
    { question: "Is it worth it to change careers at 30? 40? 50?", answer: "It can be, but the math changes with age. Younger workers have more years to recoup education costs and benefit from higher salaries. Our calculator accounts for your current age, retirement age (typically 65 in Canada), CPP benefits impact, and years of working life remaining to show whether a career change makes financial sense at your stage." },
    { question: "How much does going back to school cost in Canada?", answer: "Canadian university tuition averages $7,000-$15,000/year for domestic students, with professional programs (law, medicine, MBA) costing $20,000-$60,000/year. The true cost also includes lost income during school. For a 2-year program at $15,000/year with a $60,000 salary, the real cost is closer to $150,000 ($30K tuition + $120K lost wages)." },
    { question: "What are the highest paying careers in Canada 2026?", answer: "Top-paying careers include physicians ($250K+), dentists ($180K+), IT managers ($140K+), engineers ($100K+), and skilled trades like electricians and plumbers ($80K-$120K with overtime). Our calculator helps you compare the financial ROI of pursuing any of these paths against your current career." },
    { question: "Is skilled trades or university better in Canada?", answer: "Skilled trades (electrician, plumber, welder) often provide better ROI than many university degrees. Red Seal trades earn $60K-$120K+ with lower education costs ($5K-$15K) and paid apprenticeships. University grads may face $40K+ debt and lower starting salaries. Our calculator compares both paths with Canadian tax rates." },
    { question: "How do CPP and EI affect my take-home pay?", answer: "In 2026, CPP contributions are 5.95% on earnings up to $68,500 (max $4,078/year), plus CPP2 of 4% on earnings $68,500-$73,200. EI is 1.66% up to $63,200 (max $1,049/year). Higher-paying careers hit these caps, meaning less deduction per dollar earned above the thresholds. Our calculator includes these in all comparisons." },
    { question: "How do provincial taxes affect career decisions?", answer: "Provincial taxes vary significantly: Alberta has no provincial sales tax and lower income tax, while Quebec has higher taxes but subsidized daycare. A $100K salary in Alberta takes home ~$75K vs ~$68K in Quebec. Our calculator uses your specific province for accurate after-tax comparisons." },
    { question: "What is OSAP and how does it affect education costs?", answer: "OSAP (Ontario Student Assistance Program) provides grants and loans for Ontario students. Grants don't need repayment; loans have 0% interest while in school. Similar programs exist in other provinces (StudentAidBC, Alberta Student Aid). Our calculator helps you see if the investment pays off regardless of how you fund it." },
    { question: "Should I get a Canadian MBA or work experience?", answer: "Top Canadian MBA programs (Rotman, Ivey, Schulich) cost $80K-$120K but average $120K+ starting salaries. However, 5 years of work experience with promotions might achieve similar earnings without debt. Our calculator compares both scenarios with Canadian tax rates to show the break-even point." },
    { question: "How does the career comparison calculator account for Canadian taxes?", answer: "We calculate accurate after-tax income using federal + provincial tax rates, CPP contributions, EI premiums, and basic personal amounts. This is critical because a $100K salary in Ontario keeps ~$73K vs ~$68K in Quebec. We show both gross earnings and net take-home pay for realistic comparisons." },
    { question: "What salary increase justifies the cost of more education?", answer: "Generally, your new career should pay at least 30-50% more to justify the cost and time of education. However, it depends on program cost, duration, your age, and career growth potential. Our calculator shows exactly what salary increase you need in your specific Canadian tax situation to break even." },
    { question: "Can I compare more than two career paths?", answer: "Yes! You can save unlimited scenarios (or 2 for free users) with names like 'Doctor - Optimistic', 'Plumber - Realistic', 'MBA - Pessimistic'. Then load any two scenarios into Path A and Path B to compare them side-by-side. This lets you test multiple what-if scenarios and see which career path wins." },
    { question: "What is the year-by-year cash flow table?", answer: "Our cash flow table shows every year from now until retirement, including: opening balance, gross earnings, federal and provincial taxes, CPP/EI, education costs, net income, and closing cumulative balance. This reveals the 'pain valley' during school years and shows exactly when your new career earnings surpass your old path." },
    { question: "How does changing careers affect my CPP pension?", answer: "CPP pension is based on your 40 highest-earning years. Taking time off for education creates lower-earning years that may reduce your pension. However, higher earnings in your new career can offset this. Our calculator focuses on working-years income, but consider CPP impact for long-term planning." },
    { question: "Is it better to study part-time while working in Canada?", answer: "Part-time study lets you keep earning but extends graduation by 2-4 years. Full-time study has higher opportunity cost but faster completion. Our calculator lets you model both scenarios: enter reduced income during part-time school or zero income during full-time, and compare total lifetime earnings." }
  ];

  const usaFAQs = [
    { question: "Should I go back to school or stay in my current career?", answer: "It depends on the earning potential of your new career versus the cost and time of education. Our calculator compares your current career trajectory against getting additional education, accounting for tuition costs, student loans, lost income during school, salary growth in both paths, and lifetime earnings to show which path maximizes your financial future." },
    { question: "How do I calculate the ROI of going back to school?", answer: "Calculate total education costs (tuition + lost income) versus the salary increase in your new career over your working lifetime. Our calculator does this automatically, factoring in federal and state taxes, salary growth rates, and the opportunity cost of years spent in school to give you a clear financial comparison." },
    { question: "Is it worth it to change careers at 30? 40? 50?", answer: "It can be, but the math changes with age. Younger workers have more years to recoup education costs and benefit from higher salaries. Our calculator accounts for your current age, retirement age, and years of working life remaining to show whether a career change makes financial sense at your stage." },
    { question: "How much does going back to school really cost?", answer: "The true cost includes tuition, books, living expenses during school, AND the salary you give up while studying (opportunity cost). For a 2-year program at $15,000/year with a $60,000 current salary, the real cost is closer to $150,000 ($30K tuition + $120K lost wages), not just $30,000." },
    { question: "What salary increase justifies the cost of more education?", answer: "Generally, your new career should pay at least 30-50% more to justify the cost and time of education. However, it depends on program cost, duration, your age, and career growth potential. Our calculator shows exactly what salary increase you need in your specific situation to break even and come out ahead." },
    { question: "How does the career comparison calculator account for salary growth?", answer: "Our calculator lets you model realistic salary growth for both career paths using percentage increases, fixed dollar raises, or custom year-by-year changes. It accounts for different growth trajectories - new careers often have steeper early growth while established careers may plateau - to give you accurate lifetime earnings comparisons." },
    { question: "Should I get a master's degree or start working?", answer: "Compare the immediate earning potential of working versus the higher salary a master's degree may provide. Our calculator shows the financial breakeven point - typically 5-10 years - where the higher salary from advanced education outweighs the costs and lost income during school years." },
    { question: "How do I compare a high-paying career I hate versus lower-paying work I love?", answer: "While our calculator focuses on financial outcomes, you can model salary differences and see the dollar cost of choosing passion over pay. If Career A pays $100K and Career B pays $70K, our calculator shows the lifetime difference ($1-2M over 30 years) to help you decide if the happiness is worth the cost." },
    { question: "Does changing careers set me back financially?", answer: "Often yes, in the short term - you lose seniority, take entry-level pay, and may need education. But if the new career has better long-term growth, you can come out ahead. Our calculator shows the crossover point where your new career total earnings surpass staying in your current role." },
    { question: "How much should I expect to earn in my first year after a career change?", answer: "Expect 20-40% less than your current salary initially, even with education. You're entry-level in the new field. However, if the career has strong growth potential, you'll catch up. Our calculator lets you model this realistic salary trajectory to see when you'll surpass your old career path." },
    { question: "Does this calculator account for US taxes?", answer: "Yes! Unlike competitors that use pre-tax calculations, we calculate accurate after-tax income using federal tax brackets and state tax rates. This is critical because taxes can differ significantly between states, and after-tax income is what you actually take home. We show both gross earnings and net take-home pay." },
    { question: "Can I compare more than two career paths?", answer: "Yes! You can save unlimited scenarios (or 2 for free users) with names like 'Doctor - Optimistic', 'Plumber - Realistic', 'MBA - Pessimistic'. Then load any two scenarios into Path A and Path B to compare them side-by-side. This lets you test multiple what-if scenarios and see which career path wins under different assumptions." },
    { question: "What is the year-by-year cash flow table?", answer: "Our cash flow table shows every year of your career from now until retirement, including: opening balance, gross earnings, taxes paid, education costs, net income, and closing cumulative balance. This reveals the 'pain valley' during school years when you earn less and pay tuition, then shows exactly when your new career earnings accelerate past your old path." }
  ];

  return (
    <div className="career-comparison-calculator">
      {/* SEO Structured Data */}
      <StructuredData
        type="faq"
        data={{ faqs: isUSA ? usaFAQs : canadaFAQs }}
      />
      <StructuredData
        type="breadcrumb"
        data={{
          items: [
            { name: "Home", url: "https://larryshelp.org" },
            { name: "Career Comparison Calculator", url: "https://larryshelp.org/career-comparison" }
          ]
        }}
      />

      <div className="car-planner-header">
        <h1>Career Comparison Calculator {isUSA ? 'USA' : 'Canada'}</h1>
        <p>
          {isUSA
            ? 'The only side-by-side career path calculator with US federal & state taxes, year-by-year cash flow, and scenario saving'
            : 'The only side-by-side career path calculator with Canadian provincial taxes, CPP/EI deductions, and scenario saving'
          }
        </p>
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap',
          fontSize: '0.9rem',
          color: '#6b7280',
          marginTop: '12px'
        }}>
          <span>✅ Side-by-Side Comparison</span>
          <span>✅ {isUSA ? 'US Tax Math' : 'Canadian Tax Math'}</span>
          <span>✅ Year-by-Year Cash Flow</span>
          <span>✅ Save Scenarios</span>
          <span>✅ Education ROI</span>
        </div>
        <br/>
                        <Link
                  to="/career-comparison-info"
                  className="learn-more-btn"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More
                </Link>
      </div>

      {/* Helpful Tips Section */}
      <div style={{
        background: '#eff6ff',
        border: '2px solid #3b82f6',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        maxWidth: '90vw'
      }}>
        <h3 style={{ margin: '0 0 12px 0', color: '#1e40af', fontSize: '1.1rem' }}>💡 Quick Tips</h3>
        <div style={{ display: 'grid', gap: '12px', fontSize: '0.95rem', color: '#1e3a8a' }}>
          <div><strong>💾 Save Your Work:</strong> Name your scenario below (e.g., "Doctor vs Plumber") and click <strong>Save</strong> to keep it for later.</div>
          <div><strong>🔄 Compare What-Ifs:</strong> Save different scenarios (optimistic, realistic, pessimistic) then load them to Path A/B to compare.</div>
          {isUSA ? (
            <>
              <div><strong>📊 Find Salary Data:</strong> Use <a href="https://www.bls.gov/oes/" target="_blank" rel="noopener" style={{color: '#1d4ed8', fontWeight: '600'}}>Bureau of Labor Statistics</a>, <a href="https://www.glassdoor.com" target="_blank" rel="noopener" style={{color: '#1d4ed8', fontWeight: '600'}}>Glassdoor</a>, or <a href="https://www.payscale.com" target="_blank" rel="noopener" style={{color: '#1d4ed8', fontWeight: '600'}}>PayScale</a> for typical salaries.</div>
              <div><strong>🎓 Education Costs:</strong> Check <a href="https://collegecost.ed.gov" target="_blank" rel="noopener" style={{color: '#1d4ed8', fontWeight: '600'}}>College Navigator</a> or school websites for tuition estimates.</div>
            </>
          ) : (
            <>
              <div><strong>📊 Find Salary Data:</strong> Use <a href="https://www.jobbank.gc.ca/wagereport" target="_blank" rel="noopener" style={{color: '#1d4ed8', fontWeight: '600'}}>Job Bank</a>, <a href="https://www.glassdoor.ca" target="_blank" rel="noopener" style={{color: '#1d4ed8', fontWeight: '600'}}>Glassdoor</a>, or <a href="https://www.payscale.com" target="_blank" rel="noopener" style={{color: '#1d4ed8', fontWeight: '600'}}>PayScale</a> for typical salaries.</div>
              <div><strong>🎓 Education Costs:</strong> Check <a href="https://www.univcan.ca" target="_blank" rel="noopener" style={{color: '#1d4ed8', fontWeight: '600'}}>Universities Canada</a> or school websites for tuition estimates.</div>
            </>
          )}
        </div>
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: 'white',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
            <input
              type="checkbox"
              checked={!startSimpleMode}
              onChange={(e) => setStartSimpleMode(!e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: '600', color: '#1e40af' }}>Show Advanced Inputs</span>
            <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>(Benefits growth, detailed salary changes)</span>
          </label>
        </div>
      </div>

      {/* Section Navigation Tabs (Car Calculator pattern) */}
      <nav className="mobile-nav" style={{ marginBottom: '2rem' }}>
        {allSections.map(section => (
          <button
            key={section.id}
            className={`nav-button ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => handleSectionClick(section.id)}
          >
            {section.label}
          </button>
        ))}
      </nav>

      {/* Active Section Content */}
      {allSections.find(s => s.id === activeSection)?.render()}

      {/* Next Section Button */}
      <NextButton />


      {/* Guidance Modal */}
      <GuidanceModal
        isOpen={guidanceModal.isOpen}
        onClose={() => setGuidanceModal({ isOpen: false, title: '', content: '' })}
        onAccept={() => setGuidanceModal({ isOpen: false, title: '', content: '' })}
        title={guidanceModal.title}
        content={guidanceModal.content}
      />

      {/* Disclaimer Modal */}
      <DisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
        onAccept={async () => {
          setShowDisclaimerModal(false);
          setDisclaimerAccepted(true);
          // Supporters: never. Logged-in: 20% chance. Guests: always.
          const currentUser = TylerState.user;
          const isSupporter = currentUser && currentUser.tier === 'supporter';
          const showAd = !isSupporter && (!currentUser || Math.random() < 0.2);
          if (showAd) {
            setShowAdModal(true);
            return;
          }
          await performCalculation();
        }}
      />

      {/* Ad Modal */}
      <GoogleAdInterstitial
        isOpen={showAdModal}
        onClose={() => {
          setShowAdModal(false);
          performCalculation();  // Run calculation after ad
        }}
      />
    </div>
  );
};

export default CareerComparisonCalculator;
